import React, { useState, useRef } from 'react';
import { MdOutlineFileUpload, MdOutlineImage, MdOutlineClose } from 'react-icons/md';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { IoIosAlert } from 'react-icons/io';
import { BsFiletypePdf } from 'react-icons/bs';
import { AiOutlineFileUnknown } from 'react-icons/ai';
import { SnippetSelect } from './SnippetSelect';
import { useForm, useFieldArray, useController } from 'react-hook-form';
import {
    chakra,
    FormControl,
    FormLabel,
    HStack,
    VStack,
    Button,
    ButtonProps,
    useToast,
    Box,
    IconButton,
    Text,
    useBoolean,
    SimpleGrid,
    GridItem,
    Tooltip,
    FormErrorMessage,
} from '@chakra-ui/react';
import { useAsset } from '~/hooks';

export function MultiFileUpload({
    placeholder,
    isDisabled = false,
    colSpan = 'auto',
    errors: controlledErrors = [],
    onLoadingChange,
    supportedFileTypes,
    ...props
}: any) {
    const {
        field,
        formState: { errors },
    } = useController({
        name: props.name,
        control: props.control,
        defaultValue: [],
    });
    const error = errors?.[props?.name];

    const {
        files,
        onFileChange,
        onReplaceFile,
        remove,
        control,
        isLoading,
        loadingState,
        hasChanged,
        setChange,
    } = useMultiFileUpload({
        files: field?.value || [],
        supportedFileTypes,
    });

    React.useEffect(() => {
        field.onChange(files);
    }, [files, hasChanged]);

    // Notify parent component about loading state changes
    React.useEffect(() => {
        onLoadingChange?.(isLoading);
    }, [isLoading, onLoadingChange]);

    return (
        <GridItem colSpan={colSpan}>
            <FormControl isInvalid={Boolean(error)}>
                <SimpleGrid spacing={4} columns={6} mb={3}>
                    <GridItem colSpan={4} alignSelf="center">
                        <FormLabel margin={0}>{`Anhänge${
                            files.length ? ` (${files.length})` : ''
                        }`}</FormLabel>
                    </GridItem>
                    <GridItem colSpan={2} alignSelf="center">
                        <MultiFileUploadTrigger
                            onChange={onFileChange}
                            isLoading={isLoading}
                            isDisabled={isDisabled}
                            loadingText={loadingState.join('/')}
                        />
                    </GridItem>
                </SimpleGrid>
                <VStack align="flex-start" spacing={3} cursor="pointer">
                    {files.length === 0 && <MultiFileUploadEmpty onChange={onFileChange} />}
                    {files.map(({ dokument }: MultiFileField, idx: number) => {
                        const { name: controlledName, pfad = '' } = dokument || {};
                        const name = controlledName || '';
                        const mimeTypeError =
                            typeof error?.message === 'string' &&
                            !error?.message?.includes(dokument?.mimeType || '');

                        return (
                            <HStack
                                p={3}
                                w="full"
                                justifyContent="space-between"
                                borderRadius="md"
                                borderWidth={mimeTypeError ? '2px' : '1px'}
                                borderColor={mimeTypeError ? 'red.500' : 'gray.200'}
                                className="multlti-file-upload-item"
                                key={`${name}-${idx}`}
                                _hover={{}}
                            >
                                <HStack spacing={3} minW="0">
                                    <MultiFileUploadItemIcon
                                        name={name}
                                        hasAlert={controlledErrors.includes(idx)}
                                    />
                                    <Box w="280px">
                                        <SnippetSelect
                                            isDisabled={isDisabled}
                                            control={control as any}
                                            onSelect={setChange}
                                            name={`files.${idx}.dokument.typSnippet`}
                                            category="Dokument"
                                            property="Typ"
                                        />
                                    </Box>
                                    <PresignedUrlButton name={name} pfad={pfad} />
                                </HStack>
                                <HStack spacing={0}>
                                    <ChangeFileButton
                                        isDisabled={isDisabled}
                                        onChange={onReplaceFile(idx)}
                                    />
                                    <Tooltip label="Anhang entfernen" placement="top" hasArrow>
                                        <IconButton
                                            size="sm"
                                            variant="ghost"
                                            aria-label="Remove File"
                                            isDisabled={isDisabled}
                                            onClick={() => remove(idx)}
                                        >
                                            <MdOutlineClose />
                                        </IconButton>
                                    </Tooltip>
                                </HStack>
                            </HStack>
                        );
                    })}
                </VStack>
                <FormErrorMessage>{(error?.message as string) || ''}</FormErrorMessage>
            </FormControl>
        </GridItem>
    );
}

function PresignedUrlButton(props: { name: string; pfad: string | null; threshold?: number }) {
    const { name: controlledName, pfad = '', threshold = 26 } = props || {};
    const [cursor, setCursor] = useState<'pointer' | 'wait'>('pointer');
    const { download } = useAsset();
    const name = controlledName || '';
    const fileExtension = name?.split('.').pop() || '';
    const label =
        name.length > threshold
            ? `${name.slice(0, threshold - fileExtension?.length)}...${fileExtension}`
            : name;

    return (
        <Button
            cursor={cursor}
            onClick={async () => {
                setCursor('wait');
                const url = await download(pfad as string);
                url && window.open(url, '_blank');
                setCursor('pointer');
            }}
            minW="80px"
            maxW="fit-content"
            size="sm"
            colorScheme="blue"
            variant="link"
            px={2}
        >
            <chakra.span
                isTruncated
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                display="block"
                maxW="fit-content"
                title={name}
            >
                {label}
            </chakra.span>
        </Button>
    );
}

function ChangeFileButton({
    isDisabled,
    onChange,
}: {
    isDisabled?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
}) {
    const ref = useRef<HTMLInputElement>(null);
    const [isLoading, setIsLoading] = useBoolean();
    return (
        <>
            <Tooltip label="Anhang ersetzen" placement="top" hasArrow>
                <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label="Change File"
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                    onClick={() => ref?.current?.click()}
                >
                    <MdOutlineFileUpload />
                </IconButton>
            </Tooltip>
            <chakra.input
                onChange={async (e) => {
                    setIsLoading.on();
                    await onChange?.(e);
                    setIsLoading.off();
                }}
                ref={ref}
                type="file"
                display="none"
            />
        </>
    );
}

function MultiFileUploadItemIcon({ name, hasAlert = false }: { name: string; hasAlert?: boolean }) {
    const checkFileExtension = (regex: RegExp) => regex.test(name);
    const isImage = [/\.jpe?g$/i, /\.png$/i, /\.webp$/i, /\.tiff?$/i, /\.bmp$/i, /\.jfif$/i].some(
        checkFileExtension
    );
    const isPdf = [/\.pdf$/i].some(checkFileExtension);
    const isWord = [/\.doc$/i, /\.docx$/i, /\.odt$/i].some(checkFileExtension);

    return (
        <VStack
            p={1.5}
            borderWidth="1px"
            borderRadius="md"
            color={hasAlert ? 'orange.500' : 'gray.500'}
            bgSize="cover"
            bgPosition="center"
            bgRepeat="no-repeat"
            sx={{
                '.multlti-file-upload-item:hover &': {
                    color: hasAlert ? 'orange.500' : 'blue.500',
                    // bgImage: `url(${URL.createObjectURL(file)})`,
                },
            }}
        >
            {hasAlert ? (
                <IoIosAlert size={24} />
            ) : (
                <>
                    {isImage && <MdOutlineImage size={24} />}
                    {isPdf && <BsFiletypePdf size={24} />}
                    {isWord && <IoDocumentTextOutline size={24} />}
                    {!isImage && !isPdf && !isWord && <AiOutlineFileUnknown size={24} />}
                </>
            )}
        </VStack>
    );
}

function MultiFileUploadEmpty({
    onChange,
}: {
    onChange: React.ChangeEventHandler<HTMLInputElement>;
}) {
    const ref = useRef<HTMLInputElement>(null);

    return (
        <>
            <VStack
                w="full"
                h="66px"
                p={2}
                onClick={() => ref?.current?.click()}
                borderStyle="dashed"
                color="gray.600"
                _hover={{ color: 'gray.800' }}
                cursor="pointer"
                alignItems="center"
                justifyContent="center"
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
            >
                <Text fontSize="md" opacity={0.5}>
                    Keine Anhänge ausgewählt...
                </Text>
            </VStack>
            <chakra.input onChange={onChange} ref={ref} type="file" display="none" multiple />
        </>
    );
}

function MultiFileUploadTrigger({
    onChange,
    loadingText,
    isDisabled = false,
    isLoading = false,
}: {
    isDisabled?: boolean;
    onChange: React.ChangeEventHandler<HTMLInputElement>;
} & Omit<ButtonProps, 'onChange'>) {
    const ref = useRef<HTMLInputElement>(null);
    return (
        <>
            <Button
                isLoading={isLoading}
                loadingText={loadingText}
                onClick={() => ref?.current?.click()}
                variant="outline"
                size="md"
                w="full"
                display="flex"
                alignItems="center"
                gap="2"
                isDisabled={isDisabled}
            >
                <MdOutlineFileUpload />
                Datei auswählen
            </Button>
            <chakra.input onChange={onChange} ref={ref} type="file" display="none" multiple />
        </>
    );
}

type MultiFileField = {
    dokument?: {
        name?: string | null;
        pfad?: string | null;
        uploadDatum?: Date;
        typSnippet?: any;
        mimeType?: string | null;
    };
};

type MultiFileFormValues = {
    files: MultiFileField[];
};

type MultiFileUploadProps = {
    supportedFileTypes?: string[];
    files?: MultiFileField[];
};

const defaultSupportedFileTypes = [
    'image/png',
    'image/jpg',
    'image/jpeg',
    'image/webp',
    'image/tiff',
    'image/bmp',
    'image/jfif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.oasis.opendocument.text',
];

function useMultiFileUpload({
    supportedFileTypes = defaultSupportedFileTypes,
    files: controlledFiles = [],
}: MultiFileUploadProps = {}) {
    const [loadingState, setLoadingState] = useState([0, 0]);
    const [isLoading, setIsLoading] = useBoolean();
    const { upload } = useAsset();
    const { control, watch, setValue } = useForm<MultiFileFormValues>({
        defaultValues: { files: controlledFiles },
    });
    const [hasChanged, setHasChanged] = useState(0);
    const setChange = () => setHasChanged(Date.now());

    const { fields, append, remove, ...fieldArray } = useFieldArray<MultiFileFormValues>({
        control,
        name: 'files',
    });

    const toast = useToast();
    const files: MultiFileField[] = watch('files') || [];

    const invalidToastNotification = (file: File) =>
        toast({
            title: `Falsches Dateiformat: ${file.type}`,
            position: 'top',
            status: 'warning',
            duration: 5000,
            isClosable: true,
            description: `Bitte wählen Sie nur Dateien im folgendem Format aus: ${supportedFileTypes.join(
                ', '
            )}`,
        });

    const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsLoading.on();
        const selectedFiles = [...(Array.from(event.target.files || []) as File[])];
        for (const file of selectedFiles) {
            setLoadingState((prev) => [prev[0] + 1, selectedFiles.length]);
            const isInvalid = !supportedFileTypes.includes(file.type);
            if (isInvalid) {
                invalidToastNotification(file);
                continue;
            }

            const { key: pfad = '', name = '' } = await upload(file);

            append({ dokument: { name, pfad, uploadDatum: new Date(), mimeType: file.type } });
        }
        setIsLoading.off();
        setLoadingState([0, 0]);
        event.target.value = '';
    };

    const onReplaceFile = (idx: number) => async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = [...(Array.from(event.target.files || []) as File[])];
        const file = selectedFile?.[0];
        if (!file) return;
        const isInvalid = !supportedFileTypes.includes(file.type);
        if (isInvalid) {
            invalidToastNotification(file);
            return;
        }
        setIsLoading.on();
        setLoadingState([1, 1]);
        const { key: pfad = '', name = '' } = await upload(file);
        setValue(`files.${idx}.dokument.pfad`, pfad);
        setValue(`files.${idx}.dokument.name`, name);
        setValue(`files.${idx}.dokument.uploadDatum`, new Date());
        setValue(`files.${idx}.dokument.mimeType`, file.type);
        setIsLoading.off();
        setLoadingState([0, 0]);
        setChange?.();
    };

    return {
        onFileChange,
        supportedFileTypes,
        fields,
        files,
        remove,
        control,
        isLoading,
        loadingState,
        onReplaceFile,
        hasChanged,
        setChange,
        ...fieldArray,
    };
}
